import { isDefined } from "@clipboard-health/util-ts";
import { useGetWorkerOpenPlacementFilters } from "@src/appV2/redesign/PlacementCandidate/api/useGetWorkerOpenPlacementFilters";
import { type Worker } from "@src/appV2/Worker/api/types";

import {
  type GetPlacementsForWorkplaceQuery,
  PlacementApplicationStatus,
} from "../../../types/fetchPlacements.schema";
import { DEFAULT_DISTANCE_FILTER_IN_MILES } from "../../../utils";
import { translateFilterForBackend } from "../../../utils/filter-translation";

export const useGetPlacementFilters = (params: {
  filter: GetPlacementsForWorkplaceQuery["filter"] | undefined;
  showApplications?: boolean;
  worker: Worker;
  hasInterviews?: boolean;
  searchByWorkplaceName?: string;
  enabled: boolean;
}): GetPlacementsForWorkplaceQuery["filter"] | undefined => {
  const { filter, showApplications, worker, hasInterviews, searchByWorkplaceName, enabled } =
    params;
  const { data: availableWorkerTypes } = useGetWorkerOpenPlacementFilters(
    {
      workerId: worker.userId,
      useHiringWorkerTypes: true,
    },
    { enabled }
  );

  if (!filter || !availableWorkerTypes) {
    return undefined;
  }

  let distanceFilter: { distanceInMiles?: number } = {
    distanceInMiles: DEFAULT_DISTANCE_FILTER_IN_MILES,
  };

  // If the distance is defined and greater than 0, use the distance filter
  if (isDefined(filter?.distanceInMiles) && (filter.distanceInMiles?.$lt ?? 0) > 0) {
    distanceFilter.distanceInMiles = filter.distanceInMiles.$lt;
  } else if (showApplications) {
    // if we are showing applications, we don't need to filter by distance
    distanceFilter = {};
  }

  // Remove distanceInMiles and apply filter translation for UI fields

  const { distanceInMiles: _distanceInMiles, ...filterWithoutDistance } = filter;
  const translatedFilter = translateFilterForBackend(filterWithoutDistance);

  if (showApplications) {
    return {
      "applications.status": Object.values(PlacementApplicationStatus).join(","),
      hasInterviews,
    };
  }

  return {
    ...translatedFilter,
    ...(distanceFilter.distanceInMiles
      ? { "distanceInMiles.$lt": distanceFilter.distanceInMiles }
      : {}),
    workerTypes: isDefined(filter?.workerTypes)
      ? filter.workerTypes
      : availableWorkerTypes.join(","),
    ...(searchByWorkplaceName ? { workplaceName: searchByWorkplaceName } : undefined),
  };
};
